import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { X, ArrowRight } from "lucide-react";
import { submitContact } from "@/lib/api";

const SESSION_KEY = "rl_exit_intent_shown_v1";
const MOBILE_DWELL_MS = 30_000;

/**
 * Exit-intent CTA modal — fires ONCE per session on:
 *   Desktop: mouseleave through the top viewport edge
 *   Mobile:  after 30s dwell AND user scrolls up (scrollY delta negative + last position > 50% doc)
 * Dismiss via X / Esc / click-outside. Skips if prefers-reduced-motion (no auto-open),
 * but still available via the manual "Talk to lead" trigger if wired.
 */
export default function ExitIntentModal({ serviceName, sourcePage }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const shown = useRef(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") shown.current = true;
    } catch {}

    const trigger = () => {
      if (shown.current) return;
      shown.current = true;
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
      setOpen(true);
    };

    if (reduced) return; // opt-out of auto-open on reduced motion

    const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;

    if (!isTouch) {
      const onMouseOut = (e) => {
        if (!e.relatedTarget && e.clientY <= 4) trigger();
      };
      document.addEventListener("mouseout", onMouseOut);
      return () => document.removeEventListener("mouseout", onMouseOut);
    }

    // Mobile: 30s dwell + scroll-up past 50% of doc
    const start = Date.now();
    let lastY = window.scrollY;
    const onScroll = () => {
      const nowY = window.scrollY;
      const dwell = Date.now() - start;
      const halfway =
        nowY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight) > 0.5;
      if (dwell > MOBILE_DWELL_MS && nowY < lastY - 40 && halfway) trigger();
      lastY = nowY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [reduced]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.email.includes("@")) {
      return toast.error("Name and a valid email are required");
    }
    setBusy(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: "",
        role: "",
        interest: serviceName || "",
        message: `Exit-intent request from ${sourcePage}. Requesting a 30-minute working session with the ${serviceName || "practice"} lead.`,
        source_page: sourcePage || "/services",
      });
      toast.success("Session request received. A practice lead will reach out within one business day.");
      setOpen(false);
    } catch {
      toast.error("Something went wrong — please try /contact directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[85] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="exit-intent-modal"
        >
          <div
            className="absolute inset-0 bg-ink/85 backdrop-blur-md"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <motion.div
            initial={reduced ? { opacity: 0 } : { y: 28, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-intent-title"
            className="relative w-full max-w-lg"
          >
            {/* Gradient border via wrapper */}
            <div className="rounded-2xl p-[1px] bg-gradient-to-br from-gold via-brandPurple to-gold shadow-[0_30px_80px_-20px_rgba(245,184,65,0.35)]">
              <div className="relative glass-panel-strong rounded-[15px] p-6 md:p-8">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="absolute top-4 right-4 h-8 w-8 inline-flex items-center justify-center rounded-full text-inkText-muted hover:text-gold"
                  aria-label="Close"
                  data-testid="exit-intent-close"
                >
                  <X className="h-4 w-4" />
                </button>

                <div className="font-mono text-[10px] tracking-[0.28em] text-gold-body mb-3">
                  {(serviceName || "PRACTICE LEAD").toUpperCase()} · 30-MIN SESSION
                </div>
                <h2
                  id="exit-intent-title"
                  className="font-display font-semibold leading-[1.1] text-inkText-primary"
                  style={{ fontSize: "clamp(1.5rem, 2.6vw, 2rem)" }}
                >
                  Before you go — <span className="text-gradient-gold-violet">talk to the practice lead</span>.
                </h2>
                <p className="mt-3 text-sm text-inkText-muted leading-relaxed">
                  Book a 30-minute working session. No sales pitch — real guidance from a certified engineer on this practice.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-3">
                  {[
                    { k: "name", label: "Full name *", type: "text" },
                    { k: "email", label: "Company email *", type: "email" },
                    { k: "phone", label: "Phone", type: "tel" },
                  ].map((f) => (
                    <label key={f.k} className="block">
                      <span className="block font-mono text-[10px] tracking-[0.24em] text-inkText-muted mb-1.5">
                        {f.label.toUpperCase()}
                      </span>
                      <input
                        type={f.type}
                        required={f.label.includes("*")}
                        value={form[f.k]}
                        onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))}
                        data-testid={`exit-intent-${f.k}`}
                        className="w-full rounded-lg bg-ink/60 border border-gold/25 px-3 py-2.5 text-sm text-inkText-primary focus:outline-none focus:border-gold"
                      />
                    </label>
                  ))}
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="exit-intent-submit"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-b from-[#FFD37A] to-[#C89026] text-ink px-5 py-3 text-sm font-medium border border-gold/60 disabled:opacity-50"
                  >
                    {busy ? "Submitting…" : (
                      <>
                        Book my session <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                  <div className="pt-1 text-center">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="font-mono text-[10px] tracking-[0.24em] text-inkText-muted hover:text-inkText-primary"
                    >
                      NO THANKS
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
